import React, { useEffect, useState } from 'react';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';

import InputGroup from '../../../../components/global/InputGroup';
import Button from '../../../../components/global/Button';
import Alert from '../../../../components/global/Alert';
import Logo from '../../../../components/global/Logo';
import { getAsset } from '../../../../util/assets';
import { getErrorString } from '../../../../util/normalize-errors';
import { signupReservationCreate as signupReservationCreateMutation } from './graphql';
import SplitLayout from '../../../../components/global/SplitLayout';

const propTypes = {
  signupReservationCreate: PropTypes.func.isRequired,
  numberOfPeopleAlreadyReserved: PropTypes.number,
};

const Reservation = ({
  signupReservationCreate,
  numberOfPeopleAlreadyReserved,
}) => {
  const [username, setUsername] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [formValidationError, setFormValidationError] = useState('');
  const [validForm, setFormValidity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Check username is valid (if entered).
    setFormValidationError(
      username !== '' && !username.match(/^\w+$/)
        ? `Your username can only contain letters, numbers and '_'.`
        : '',
    );
  }, [username]);

  useEffect(() => {
    // All fields entered, with no validation errors.
    setFormValidity(
      username !== '' && emailAddress !== '' && formValidationError === '',
    );
  }, [username, emailAddress]);

  const attemptReservation = async () => {
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      await signupReservationCreate(username, emailAddress);

      setSuccess('Succesfully reserved a spot in Shutterbug.');
      // todo: add a redirect when there is a page to redirect to
    } catch (err) {
      setError(getErrorString(err));
    }
    setLoading(false);
  };

  return (
    <div className="shutterbug-reservation">
      <SplitLayout.Wrapper>
        <div className="branding-space">
          <Logo />
          <span className="beta-tag">beta</span>
        </div>

        <h2 className="reservation-header">Reserve your username</h2>

        <div className="reservation-background-picture-mobile mb-3" />

        <p className="intro-text">
          In order to provide a robust, world-class photography experience, we
          are filling reservations on a first-come, first-served basis.
        </p>
        <form
          className="reservation-form"
          onSubmit={event => {
            event.preventDefault();
            attemptReservation();
          }}
        >
          <InputGroup
            id="username"
            type="text"
            name="username"
            addon={<img alt="" src={getAsset('/shutterbug/icons/user.svg')} />}
            placeholder="@username..."
            value={username}
            onChange={({ target: { value } }) => setUsername(value)}
            wrapperClassName="mb-2"
          />

          <InputGroup
            id="email"
            type="email"
            name="email"
            addon={<img alt="" src={getAsset('/shutterbug/icons/email.svg')} />}
            placeholder="Your email address..."
            value={emailAddress}
            onChange={({ target: { value } }) => setEmailAddress(value)}
          />

          {formValidationError && (
            <Alert type="warning" dismissible>
              {formValidationError}
            </Alert>
          )}

          {error && (
            <Alert type="danger" dismissible>
              <strong>Reserving a spot wasn&#39;t successful</strong>
              <br />
              {error}
            </Alert>
          )}

          {success && <Alert type="success">{success}</Alert>}

          <Button
            type="submit"
            className="btn-block mt-4"
            disabled={loading || !validForm}
          >
            Reserve my spot
          </Button>
        </form>
        {numberOfPeopleAlreadyReserved && (
          <div className="already-reserved-display">
            <div className="number-ahead">{numberOfPeopleAlreadyReserved}</div>
            <div className="label-ahead">People in front of you</div>
          </div>
        )}
      </SplitLayout.Wrapper>
    </div>
  );
};

const withSignupReservation = graphql(signupReservationCreateMutation, {
  props: ({ mutate }) => ({
    signupReservationCreate: (username, emailAddress) =>
      mutate({
        variables: {
          input: {
            username,
            email_address: emailAddress,
            type: 'SHUTTERBUG',
          },
        },
      }),
  }),
});

Reservation.propTypes = propTypes;

export { Reservation as ReservationComponent };

export default compose(withSignupReservation)(Reservation);
